<template>
  <div class="flex flex-col h-screen">
    <ria-navbar id="header-section" v-if="showNavbar"/>
    <div id="spinner-loading" class="fixed top-0 right-0 bottom-0 left-0 flex justify-center items-center bg-black bg-opacity-50 z-60" v-if="loadingRequest">
      <div class="spinner">
        <div class="bounce1"></div>
        <div class="bounce2"></div>
        <div class="bounce3"></div>
      </div>
      <div v-if="isResults">
        <p class="text-white text-xl font-medium pt-2">{{$t('purchase.processing')}}</p>
      </div>
    </div>
    <span v-if="testingMode" class="text-center text-xl text-red">{{$t('testing.text')}}</span>
    <vue-page-transition id="main-section" name="fade" class="transition" v-bind:class="{ 'testing': testingMode }">
      <router-view />
    </vue-page-transition>
    <ria-footer/>
    <modal v-if="showModal" :closeButtonText="$t('review.ok')" @close="closeModal" >
        <div slot="title">
            <span>{{ modal.title }}</span>
        </div>
        <div slot="body">
            <span>{{ modal.body }}</span>
        </div>
    </modal>
  </div>
</template>

<script>
  import RiaNavbar from "@/components/layout/ria-navbar.vue";
  import RiaFooter from "@/components/layout/ria-footer.vue";
  import Modal from "@/components/common/modal"
  import { mapGetters, mapMutations } from 'vuex';
  import { getPlatformType } from '@/helpers/deviceHelpers';
  import { routesWithoutDesktopNavigation, routesWithoutMobileNavigation, routesWithWhiteBackground } from "@/helpers/routesHelpers";
  import riaApiClient from "@/api";
  import { setPrimaryColor, setAccentColor } from "@/helpers/themeHelpers";
  import { setOptions, bootstrap } from 'vue-gtag'


  export default {
    name: "app",
    metaInfo() {
        return { 
            title: this.$t('placeholder.meta.title'),
            meta: [
                { name: 'description', content:  this.$t('placeholder.meta.description')}, 
            ]
        }
    },
    components: {
      RiaNavbar,
      RiaFooter,
      Modal
    },
    data: function() {
      return {
        showModal: false,
        modal: {
          title: '',
          body: ''
        },
        loadingRequest: false,
        gtagReady: false,
        pendingPurchase: null
      }
    },
    computed: {
      ...mapGetters("device", ["size", "platform", "initialWidth", "initialHeight"]),
      ...mapGetters("resort", ["resort", "environment"]),
      ...mapGetters("auth", ["testingMode"]),
      showNavbar: function() {
        return !(this.size === 'lg' ? routesWithoutDesktopNavigation.includes(this.$route.name) : routesWithoutMobileNavigation.includes(this.$route.name));
      },
      isResults: function(){
        return routesWithWhiteBackground.includes(this.$route.name)
      }
    },
    methods: {
      ...mapMutations("device", ["SET_LOCALE", "SET_PLATFORM", "SET_SIZE", "SET_INITIAL_WIDTH", "SET_INITIAL_HEIGHT"]),
      closeModal: function() {
        this.showModal = false
      },
      onResize() {
        this.SET_SIZE(window.innerWidth)
      },
      async initGtag() {
        let me = this

        setOptions({
          config: { id: this.environment.analytics },
          onReady: function () {
            me.gtagReady = true
          },
          pageTrackerTemplate(to) {
            const realPath = to.meta.analytics ? to.meta.analytics.realPath(to) : to.fullPath;
            return {
              page_title: to.name, 
              page_path: realPath, 
              page_location: realPath 
            }
          }
        })
        await bootstrap()
      },
      submitPruchase(purchase) {
        this.$gtag.purchase(purchase)
      }
    },
    created() {
      window.addEventListener('showSpinner', () => this.loadingRequest = true)
      window.addEventListener('hideSpinner', () => this.loadingRequest = false)
      if (this.environment) {
        this.initGtag();
      }

      this.$root.$once('submitPurchase', (purchase) => {
        if (!this.gtagReady) {
          this.pendingPurchase = purchase
          return;
        }

        this.submitPruchase(purchase)
      })

      window.addEventListener('errorModal', (payload) => {
        this.modal = payload.detail
        this.showModal = true
      });

      riaApiClient.interceptors.request.use((config) => {
        this.loadingRequest = true;
        return config;
      }, (error) => {
        this.loadingRequest = false;
        return Promise.reject(error);
      });

      riaApiClient.interceptors.response.use((response) => {
        this.loadingRequest = false;
        return response;
      }, (error) => {
        this.loadingRequest = false;
        return Promise.reject(error);
      });

    },
    mounted() {
      this.SET_LOCALE(navigator.language)
      this.SET_PLATFORM(getPlatformType())
      this.SET_SIZE(window.innerWidth)
      this.SET_INITIAL_WIDTH(window.innerWidth)
      this.SET_INITIAL_HEIGHT(window.innerHeight)

      if(this.resort) {
        setPrimaryColor(this.resort.primary_color)
        setAccentColor(this.resort.accent_color)
      }

      if (/iPad|iPhone|iPod/.test(navigator.userAgent) || getPlatformType() == 'IOS') {
        window.document.addEventListener('touchmove', e => {
          if(e.scale !== 1) {
            e.preventDefault();
          }
        }, {passive: false});
      }

      window.addEventListener('resize', this.onResize)
      
      if(getPlatformType() === 'BROWSER'){
        this.$cookiebot.consentBanner({
          locale: this.$i18n.locale
        })
      }
    },
    watch: {
      "resort.primary_color"(color) {
        setPrimaryColor(color);
      },
      "resort.accent_color"(color) {
        setAccentColor(color);
      },
      environment: function() {
        if (!this.gtagReady) {
          this.initGtag();
        }
      },
      gtagReady: function() {
        if (this.pendingPurchase) {
          this.submitPruchase(this.pendingPurchase);
          this.pendingPurchase = null
        }
      }
    }
  };
</script>
<style scoped>
#spinner-loading{
  flex-flow: column;
}

#main-section {
  @apply mx-0;
}

#header-section {
  @apply fixed top-0 left-0 w-full p-2 z-10 bg-grey-bg flex content-center justify-between items-center;
}

@screen lg {
  #header-section {
    @apply relative h-20 bg-primary p-5
  }

  #main-section {
    @apply mx-auto;
  }
}
.spinner {
  width: 54px;
}

.spinner > div {
  width: 18px;
  height: 18px;
  background-color: white;

  border-radius: 100%;
  display: inline-block;
  -webkit-animation: sk-bouncedelay 1.4s infinite ease-in-out both;
  animation: sk-bouncedelay 1.4s infinite ease-in-out both;
}

.spinner .bounce1 {
  -webkit-animation-delay: -0.32s;
  animation-delay: -0.32s;
}

.spinner .bounce2 {
  -webkit-animation-delay: -0.16s;
  animation-delay: -0.16s;
}

@-webkit-keyframes sk-bouncedelay {
  0%, 80%, 100% { -webkit-transform: scale(0) }
  40% { -webkit-transform: scale(1.0) }
}

@keyframes sk-bouncedelay {
  0%, 80%, 100% {
    -webkit-transform: scale(0);
    transform: scale(0);
  } 40% {
    -webkit-transform: scale(1.0);
    transform: scale(1.0);
  }
}
</style>
